import type { Resolver, FieldValues, FieldErrors, FieldError } from 'react-hook-form';
import type { z } from 'zod';

/** Replacement for `@hookform/resolvers/zod`. The official resolver checks
 *  `error instanceof z4.$ZodError`, which breaks under Vite ESM when the
 *  schema is created from `zod` (main entry) and the resolver imports from
 *  `zod/v4/core` — class identity diverges across module copies and the
 *  resolver rethrows, leaving the form stuck in `isSubmitting`. Using
 *  `safeParseAsync` sidesteps the issue. */
export function zodFormResolver<T extends FieldValues>(
  schema: z.ZodObject<z.ZodRawShape>,
): Resolver<T> {
  return async (values: T) => {
    const result = await schema.safeParseAsync(values);
    if (result.success) {
      return { values: result.data as T, errors: {} };
    }
    const errors: Record<string, FieldError> = {};
    for (const issue of result.error.issues) {
      const path = issue.path.join('.') || 'root';
      if (!errors[path]) {
        errors[path] = { type: issue.code, message: issue.message };
      }
    }
    return { values: {}, errors: errors as FieldErrors<T> };
  };
}
